import { useEffect, useRef, useState } from 'react';
import api from '../api/client.js';

/**
 * Autocompleta datos de contribuyente al digitar cedula / juridica / DIMEX.
 * Consulta al backend → API publica de Hacienda (con cache).
 *
 * @param {string} identificacion valor del input (puede tener guiones)
 * @param {{ enabled?: boolean, minLength?: number, delayMs?: number }} opts
 */
export default function useContribuyenteLookup(identificacion, opts = {}) {
  const { enabled = true, minLength = 9, delayMs = 550 } = opts;
  const [estado, setEstado] = useState('idle'); // idle | loading | ok | error | not_found
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');
  const ultima = useRef('');
  const timer = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;

    const digitos = String(identificacion || '').replace(/\D/g, '');
    clearTimeout(timer.current);

    if (digitos.length < minLength || digitos.length > 12) {
      setEstado('idle');
      setDatos(null);
      setError('');
      ultima.current = '';
      return undefined;
    }

    // Solo consulta cuando el largo es valido (9–12)
    if (!/^\d{9,12}$/.test(digitos)) {
      setEstado('idle');
      return undefined;
    }

    // ultima se actualiza solo en exito: evita reconsultar la misma cedula
    if (digitos === ultima.current) {
      return undefined;
    }

    timer.current = setTimeout(async () => {
      setEstado('loading');
      setError('');
      try {
        const { data } = await api.get(`/contribuyentes/${digitos}`);
        ultima.current = digitos;
        setDatos(data.data);
        setEstado('ok');
      } catch (err) {
        const status = err.response?.status;
        const msg = err.response?.data?.message || 'No se pudo consultar Hacienda';
        setDatos(null);
        if (status === 404) {
          setEstado('not_found');
          setError(msg);
        } else {
          setEstado('error');
          setError(msg);
        }
      }
    }, delayMs);

    return () => clearTimeout(timer.current);
  }, [identificacion, enabled, minLength, delayMs]);

  const limpiar = () => {
    setEstado('idle');
    setDatos(null);
    setError('');
    ultima.current = '';
  };

  return { estado, datos, error, limpiar, cargando: estado === 'loading' };
}
